import React, { useState, useEffect, useRef, useCallback } from 'react'
import styles from './ShopPage.module.scss'
import {
  getShopProducts, getShopCategories, getShopCart, addToCart,
  updateCartItem, removeCartItem, createOrderFromCart, getShopOrders, getShopProduct,
  getProductComments, addProductComment, deleteProductComment
} from '../../../service/ShopService'
import Loader from '../../../components/Loader/Loader'

/* ─── mini icons ─── */
const CartSvg = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 3H5.5L5.6 3.5M7 13H17L21 5H6.5M7 13L5.6 3.5M7 13L5.5 15.5"/>
    <circle cx="7" cy="19" r="1" fill="currentColor" stroke="none"/>
    <circle cx="17" cy="19" r="1" fill="currentColor" stroke="none"/>
  </svg>
)
const TrashSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/>
    <path d="m19 6-.867 14.142A2 2 0 0 1 16.138 22H7.862a2 2 0 0 1-1.995-1.858L5 6m5 0V4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2"/>
  </svg>
)
const CheckSvg = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
)
const BoxSvg = () => (
  <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
    <line x1="12" y1="22.08" x2="12" y2="12"/>
  </svg>
)

/* ─── helpers ─── */
const fmt = (price) => Math.round(parseFloat(price || 0)).toLocaleString('ru-RU') + '\u00a0сом'

// iOS-safe date parser — handles "DD.MM.YYYY HH:MM" and ISO strings
const fmtDate = (raw, opts) => {
  if (!raw) return ''
  let d
  const m = String(raw).match(/^(\d{2})\.(\d{2})\.(\d{4})(?:[\s T](\d{2}):(\d{2}))?/)
  if (m) {
    d = new Date(`${m[3]}-${m[2]}-${m[1]}T${m[4] || '00'}:${m[5] || '00'}:00`)
  } else {
    // ISO format: replace space with T for iOS
    d = new Date(String(raw).replace(' ', 'T'))
  }
  if (isNaN(d)) return raw
  return d.toLocaleDateString('ru-RU', opts)
}

const PLACEHOLDER = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjMWExYTFhIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM0NDQiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj7QotC+0LLQsNGAPC90ZXh0Pjwvc3ZnPg=='

const STATUS_INFO = {
  created:    { label: 'Создан',      color: '#667eea' },
  processing: { label: 'В обработке', color: '#F59E0B' },
  shipped:    { label: 'Отправлен',   color: '#10B981' },
  delivered:  { label: 'Доставлен',   color: '#4CAF50' },
  completed:  { label: 'Завершён',    color: '#22c55e' },
  cancelled:  { label: 'Отменён',     color: '#ef4444' },
}

const ShopPage = ({ embedded = false }) => {
  const [view, setView] = useState('catalog') // catalog | detail | cart | checkout | success | orders
  const [products, setProducts] = useState([])
  const [categories, setCategories] = useState([])
  const [cart, setCart] = useState({ items: [] })
  const [orders, setOrders] = useState([])
  const [ordersLoading, setOrdersLoading] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [lastOrder, setLastOrder] = useState(null)
  const [activeCategory, setActiveCategory] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [sortBy, setSortBy] = useState('-created_at')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [cartLoadingFor, setCartLoadingFor] = useState({})
  const [error, setError] = useState(null)
  const [addedItems, setAddedItems] = useState(new Set())
  const [inCartIds, setInCartIds] = useState(new Set())
  const [checkoutForm, setCheckoutForm] = useState({
    name: '', phone: '', city: 'Бишкек', address: '', apartment: '', notes: ''
  })
  const [checkoutErrors, setCheckoutErrors] = useState({})
  const [checkoutLoading, setCheckoutLoading] = useState(false)
  const [activeImgIdx, setActiveImgIdx] = useState(0)
  const [lightboxImg, setLightboxImg] = useState(null)
  const [descExpanded, setDescExpanded] = useState(false)
  const searchTimerRef = useRef(null)
  const [isAuth, setIsAuth] = useState(!!localStorage.getItem('access_token'))

  /* ── bootstrap ── */
  useEffect(() => {
    loadCategories()
    if (localStorage.getItem('access_token')) loadCart()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /* ── react to login/logout ── */
  useEffect(() => {
    const handleAuth = () => {
      const authed = !!localStorage.getItem('access_token')
      setIsAuth(authed)
      if (authed) {
        loadCart()
        loadProducts()
      }
    }
    window.addEventListener('userLoggedIn', handleAuth)
    window.addEventListener('authSuccess', handleAuth)
    window.addEventListener('storage', handleAuth)
    return () => {
      window.removeEventListener('userLoggedIn', handleAuth)
      window.removeEventListener('authSuccess', handleAuth)
      window.removeEventListener('storage', handleAuth)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /* ── debounce search ── */
  useEffect(() => {
    clearTimeout(searchTimerRef.current)
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery)
      setPage(1)
    }, 400)
    return () => clearTimeout(searchTimerRef.current)
  }, [searchQuery])

  /* ── reload products when filters change ── */
  useEffect(() => {
    loadProducts()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory, debouncedSearch, sortBy, page])

  /* ── sync inCartIds from cart ── */
  useEffect(() => {
    if (cart?.items) {
      setInCartIds(new Set(cart.items.map(i => i.product?.id).filter(Boolean)))
    }
  }, [cart])

  /* ── API loaders ── */
  const loadCategories = async () => {
    try {
      const res = await getShopCategories()
      setCategories(res.data.results || res.data || [])
    } catch (e) {}
  }

  const loadCart = async () => {
    try {
      const res = await getShopCart()
      setCart(res.data || { items: [] })
    } catch (e) {
      setCart({ items: [] })
    }
  }

  const loadOrders = async () => {
    setOrdersLoading(true)
    try {
      const res = await getShopOrders()
      setOrders(res.data.results || res.data || [])
    } catch (e) {
      setOrders([])
    } finally {
      setOrdersLoading(false)
    }
  }

  const loadProducts = async () => {
    setLoading(true)
    setError(null)
    try {
      const params = { ordering: sortBy, page, page_size: 12 }
      if (activeCategory) params.category = activeCategory
      if (debouncedSearch) params.search = debouncedSearch
      const res = await getShopProducts(params)
      const data = res.data
      const allProducts = data.results || data || []
      setProducts(allProducts.filter(p => p.stock > 0))
      const count = data.count || 0
      setTotalCount(count)
      setTotalPages(data.total_pages || Math.ceil(count / 12) || 1)
    } catch (e) {
      setError('Ошибка загрузки товаров. Попробуйте позже.')
    } finally {
      setLoading(false)
    }
  }

  /* ── cart actions ── */
  const handleAddToCart = async (product, e) => {
    e?.stopPropagation()
    if (!isAuth) return
    const pid = product.id
    setCartLoadingFor(prev => ({ ...prev, [pid]: true }))
    try {
      await addToCart(product.slug || product.id, 1)
      setAddedItems(prev => new Set([...prev, pid]))
      await loadCart()
      setTimeout(() => {
        setAddedItems(prev => { const n = new Set(prev); n.delete(pid); return n })
      }, 2500)
    } catch (e) {
      console.error('addToCart error:', e)
    } finally {
      setCartLoadingFor(prev => ({ ...prev, [pid]: false }))
    }
  }

  const handleProductClick = async (product) => {
    setSelectedProduct(product)
    setActiveImgIdx(0)
    setDescExpanded(false)
    setView('detail')
    try {
      const res = await getShopProduct(product.slug)
      setSelectedProduct(res.data)
      setActiveImgIdx(0)
    } catch (e) {}
  }

  const handleUpdateQty = async (itemId, newQty) => {
    if (newQty < 1) { handleRemoveItem(itemId); return }
    try {
      await updateCartItem(itemId, newQty)
      setCart(prev => ({
        ...prev,
        items: prev.items.map(i => i.id === itemId ? { ...i, quantity: newQty } : i)
      }))
    } catch (e) {}
  }

  const handleRemoveItem = async (itemId) => {
    try {
      await removeCartItem(itemId)
      setCart(prev => ({ ...prev, items: prev.items.filter(i => i.id !== itemId) }))
    } catch (e) {}
  }

  /* ── checkout ── */
  const validateCheckout = () => {
    const errs = {}
    if (!checkoutForm.name.trim()) errs.name = 'Обязательное поле'
    if (!checkoutForm.phone.trim()) errs.phone = 'Обязательное поле'
    else if (!/^\+?[\d\s\-(]{7,}$/.test(checkoutForm.phone)) errs.phone = 'Введите корректный номер'
    if (!checkoutForm.address.trim()) errs.address = 'Обязательное поле'
    setCheckoutErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleSubmitOrder = async () => {
    if (!validateCheckout()) return
    setCheckoutLoading(true)
    try {
      const f = checkoutForm
      const notesText = [
        `Имя: ${f.name}`,
        `Телефон: ${f.phone}`,
        f.city && `Город: ${f.city}`,
        `Адрес: ${f.address}`,
        f.apartment && `Кв/Офис: ${f.apartment}`,
        f.notes && `Примечание: ${f.notes}`
      ].filter(Boolean).join('\n')
      const res = await createOrderFromCart({ notes: notesText })
      setLastOrder(res.data)
      setCart({ items: [] })
      setCheckoutForm({ name: '', phone: '', city: 'Бишкек', address: '', apartment: '', notes: '' })
      setView('success')
    } catch (e) {
      // "Корзина пуста" означает что заказ создан, бек очистил корзину — это успех
      const errMsg = e?.response?.data?.error || e?.message || ''
      if (errMsg === 'Корзина пуста') {
        setLastOrder(null)
        setCart({ items: [] })
        setCheckoutForm({ name: '', phone: '', city: 'Бишкек', address: '', apartment: '', notes: '' })
        setView('success')
      } else {
        setCheckoutErrors({ submit: 'Ошибка при оформлении. Попробуйте снова.' })
      }
    } finally {
      setCheckoutLoading(false)
    }
  }

  /* ── derived ── */
  const cartCount = cart?.items?.length || 0
  const cartTotal = cart?.items?.reduce(
    (sum, i) => sum + parseFloat(i.unit_price_snapshot || i.product?.price || 0) * i.quantity, 0
  ) || 0
  const isCatalogView = view === 'catalog' || view === 'detail'

  /* ════════════ RENDER ════════════ */
  return (
    <div className={styles.shopPage}>

      {/* ─── TOP NAV ─── */}
      <nav className={styles.topNav}>
        <div className={styles.navBrand}>
          <CartSvg />
          <span>NCORE Shop</span>
        </div>
        <div className={styles.navTabs}>
          <button
            className={`${styles.navTab} ${isCatalogView ? styles.navTabActive : ''}`}
            onClick={() => { setView('catalog'); setSelectedProduct(null) }}
          >
            Каталог
            {totalCount > 0 && <span className={styles.navTabCount}>{totalCount}</span>}
          </button>
          <button
            className={`${styles.navTab} ${(view === 'cart' || view === 'checkout') ? styles.navTabActive : ''}`}
            onClick={() => setView('cart')}
          >
            Корзина
            {cartCount > 0 && <span className={styles.cartBadge}>{cartCount}</span>}
          </button>
          {isAuth && (
            <button
              className={`${styles.navTab} ${view === 'orders' ? styles.navTabActive : ''}`}
              onClick={() => { setView('orders'); loadOrders() }}
            >
              Мои заказы
            </button>
          )}
        </div>
      </nav>

      {/* ─── MAIN CONTENT ─── */}
      <div className={styles.content}>

        {/* ══ CATALOG ══ */}
        {view === 'catalog' && (
          <div className={styles.catalogLayout}>
            <aside className={styles.sidebar}>
              <div className={styles.sidebarTitle}>Категории</div>
              <button
                className={`${styles.catBtn} ${!activeCategory ? styles.catBtnActive : ''}`}
                onClick={() => { setActiveCategory(null); setPage(1) }}
              >
                <span>Все товары</span>
              </button>
              {categories.map(cat => (
                <button
                  key={cat.id}
                  className={`${styles.catBtn} ${activeCategory === cat.id ? styles.catBtnActive : ''}`}
                  onClick={() => { setActiveCategory(cat.id); setPage(1) }}
                >
                  <span>{cat.name}</span>
                  {cat.products_count > 0 && <span className={styles.catCount}>{cat.products_count}</span>}
                </button>
              ))}
            </aside>

            <div className={styles.mainArea}>
              <div className={styles.controlsBar}>
                <div className={styles.searchBox}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className={styles.searchIcon}>
                    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                  </svg>
                  <input
                    type="text"
                    placeholder="Поиск товаров..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className={styles.searchInput}
                  />
                  {searchQuery && (
                    <button className={styles.clearSearch} onClick={() => setSearchQuery('')}>×</button>
                  )}
                </div>
                <select
                  value={sortBy}
                  onChange={e => { setSortBy(e.target.value); setPage(1) }}
                  className={styles.sortSelect}
                >
                  <option value="-created_at">Сначала новые</option>
                  <option value="created_at">Сначала старые</option>
                  <option value="price">Цена: ↑</option>
                  <option value="-price">Цена: ↓</option>
                  <option value="-rating_avg">По рейтингу</option>
                </select>
              </div>

              {error ? (
                <div className={styles.stateBox}>
                  <p>{error}</p>
                  <button onClick={loadProducts} className={styles.retryBtn}>Повторить</button>
                </div>
              ) : loading ? (
                <div className={styles.loaderWrap}><Loader /></div>
              ) : products.length === 0 ? (
                <div className={styles.stateBox}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                  </svg>
                  <p>Товары не найдены</p>
                </div>
              ) : (
                <>
                  <div className={styles.productsGrid}>
                    {products.map(product => {
                      const isAdded = addedItems.has(product.id)
                      const isInCart = inCartIds.has(product.id)
                      const isLoadingThis = cartLoadingFor[product.id]
                      const hasDiscount = product.discount_price && parseFloat(product.discount_price) < parseFloat(product.price)
                      const discPct = hasDiscount ? Math.round((1 - product.discount_price / product.price) * 100) : 0

                      return (
                        <div key={product.id} className={styles.productCard} onClick={() => handleProductClick(product)}>
                          <div className={styles.cardImgWrap}>
                            <img
                              src={product.main_image || PLACEHOLDER}
                              alt={product.name}
                              className={styles.cardImg}
                              onError={e => { e.target.src = PLACEHOLDER }}
                            />
                            {hasDiscount && <div className={styles.discBadge}>−{discPct}%</div>}
                            {product.stock <= 0 && <div className={styles.soldOutOverlay}>Нет в наличии</div>}
                          </div>
                          <div className={styles.cardBody}>
                            <div className={styles.cardCatRow}>
                              <span className={styles.cardCat}>{product.category_name}</span>
                              {product.shop?.logo && (
                                <img
                                  src={product.shop.logo}
                                  alt={product.shop.name}
                                  className={styles.cardStoreLogo}
                                />
                              )}
                            </div>
                            <h3 className={styles.cardName}>{product.name}</h3>
                            {product.short_description && <p className={styles.cardDesc}>{product.short_description}</p>}
                            <div className={styles.cardMeta}>
                              <div className={styles.cardRating}>
                                {parseFloat(product.rating_avg || 0) === 0 ? (
                                  <span className={styles.noRatingCard}>Нет оценок</span>
                                ) : (
                                  <>
                                    <span className={styles.star}>★</span>
                                    <span>{parseFloat(product.rating_avg).toFixed(1)}</span>
                                    {product.reviews_count > 0 && <span className={styles.revCount}>({product.reviews_count})</span>}
                                  </>
                                )}
                              </div>
                              <div className={`${styles.stockPill} ${product.stock > 0 ? styles.inStock : styles.noStock}`}>
                                {product.stock > 0 ? 'В наличии' : 'Нет'}
                              </div>
                            </div>
                            <div className={styles.cardFooter}>
                              <div className={styles.priceBlock}>
                                {hasDiscount ? (
                                  <>
                                    <span className={styles.priceNew}>{fmt(product.discount_price)}</span>
                                    <span className={styles.priceOld}>{fmt(product.price)}</span>
                                  </>
                                ) : (
                                  <span className={styles.price}>{fmt(product.current_price || product.price)}</span>
                                )}
                              </div>
                              {!isAuth ? (
                                <button
                                  className={styles.addBtn}
                                  onClick={e => { e.stopPropagation(); window.dispatchEvent(new Event('openAuth')) }}
                                >🔑 Войти</button>
                              ) : (
                                <button
                                  className={`${styles.addBtn} ${isAdded ? styles.addBtnDone : ''} ${isInCart && !isAdded ? styles.addBtnInCart : ''}`}
                                  onClick={e => handleAddToCart(product, e)}
                                  disabled={product.stock <= 0 || isLoadingThis}
                                >
                                  {isLoadingThis ? '…'
                                    : isAdded ? '✓ В корзине'
                                    : isInCart ? '+ Ещё'
                                    : product.stock <= 0 ? 'Нет'
                                    : '+ В корзину'}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                  {totalPages > 1 && (
                    <div className={styles.pagination}>
                      <button className={styles.pageBtn} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>←</button>
                      {[...Array(Math.min(totalPages, 7))].map((_, i) => (
                        <button
                          key={i + 1}
                          className={`${styles.pageBtn} ${page === i + 1 ? styles.pageBtnActive : ''}`}
                          onClick={() => setPage(i + 1)}
                        >{i + 1}</button>
                      ))}
                      <button className={styles.pageBtn} disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>→</button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* ══ PRODUCT DETAIL ══ */}
        {view === 'detail' && selectedProduct && (
          <div className={styles.detailView}>
            <button className={styles.backBtn} onClick={() => setView('catalog')}>← Назад в каталог</button>
            <div className={styles.detailLayout}>
              <div className={styles.detailImgCol}>
                {(() => {
                  const allImgs = [
                    { url: selectedProduct.main_image || PLACEHOLDER, id: 'main' },
                    ...((selectedProduct.images || []).map(img => ({ url: img.image || img.url, id: img.id })))
                  ].filter((img, idx, arr) => img.url && (idx === 0 || img.url !== arr[0].url))
                  const activeImg = allImgs[activeImgIdx] || allImgs[0]
                  return (
                    <>
                      <div className={styles.detailMainImgWrap} onClick={() => setLightboxImg(activeImg?.url || PLACEHOLDER)}>
                        <img
                          src={activeImg?.url || PLACEHOLDER}
                          alt={selectedProduct.name}
                          className={styles.detailMainImg}
                          onError={e => { e.target.src = PLACEHOLDER }}
                        />
                        <div className={styles.zoomHint}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
                          Увеличить
                        </div>
                        {selectedProduct.discount_price && parseFloat(selectedProduct.discount_price) < parseFloat(selectedProduct.price) && (
                          <div className={styles.discBadge}>
                            −{Math.round((1 - selectedProduct.discount_price / selectedProduct.price) * 100)}%
                          </div>
                        )}
                      </div>
                      {allImgs.length > 1 && (
                        <div className={styles.thumbsRow}>
                          {allImgs.map((img, idx) => (
                            <img
                              key={img.id}
                              src={img.url}
                              alt=""
                              className={`${styles.thumb} ${idx === activeImgIdx ? styles.thumbActive : ''}`}
                              onClick={() => setActiveImgIdx(idx)}
                              onError={e => { e.target.src = PLACEHOLDER }}
                            />
                          ))}
                        </div>
                      )}
                    </>
                  )
                })()}
              </div>
              <div className={styles.detailInfoCol}>
                <div className={styles.detailCat}>{selectedProduct.category_name}</div>
                <h1 className={styles.detailName}>{selectedProduct.name}</h1>
                <div className={styles.detailRating}>
                  {parseFloat(selectedProduct.rating_avg || 0) === 0 ? (
                    <span className={styles.noRatings}>Нет оценок</span>
                  ) : (
                    <>
                      {[1,2,3,4,5].map(s => (
                        <span key={s} className={s <= Math.round(selectedProduct.rating_avg || 0) ? styles.starFilled : styles.starEmpty}>★</span>
                      ))}
                      <span className={styles.ratingVal}>{parseFloat(selectedProduct.rating_avg).toFixed(1)}</span>
                      <span className={styles.ratingRevs}>
                        {selectedProduct.reviews_count || 0}
                        {' '}{(() => {
                          const c = selectedProduct.reviews_count || 0
                          if (c === 1) return 'отзыв'
                          if (c >= 2 && c <= 4) return 'отзыва'
                          return 'отзывов'
                        })()}
                      </span>
                    </>
                  )}
                </div>
                <div className={styles.detailPrices}>
                  {selectedProduct.discount_price && parseFloat(selectedProduct.discount_price) < parseFloat(selectedProduct.price) ? (
                    <>
                      <span className={styles.detailPriceNew}>{fmt(selectedProduct.discount_price)}</span>
                      <span className={styles.detailPriceOld}>{fmt(selectedProduct.price)}</span>
                      <span className={styles.detailSavings}>
                        Выгода: {fmt(selectedProduct.price - selectedProduct.discount_price)}
                      </span>
                    </>
                  ) : (
                    <span className={styles.detailPrice}>{fmt(selectedProduct.current_price || selectedProduct.price)}</span>
                  )}
                </div>
                <div className={`${styles.detailStock} ${selectedProduct.stock > 0 ? styles.detailStockIn : styles.detailStockOut}`}>
                  {selectedProduct.stock > 0 ? `✓ В наличии · ${selectedProduct.stock} шт.` : '✗ Нет в наличии'}
                </div>
                {selectedProduct.description && (() => {
                  const cleanDesc = selectedProduct.description.replace(/^"+|"+$/g, '').replace(/\r\n/g, '\n').trim()
                  const lines = cleanDesc.split('\n')
                  const isLong = lines.length > 5
                  const preview = isLong ? lines.slice(0, 5).join('\n') : cleanDesc
                  return (
                    <div className={styles.detailDesc}>
                      <h4>Описание</h4>
                      <p style={{ whiteSpace: 'pre-line' }}>
                        {descExpanded || !isLong ? cleanDesc : preview}
                      </p>
                      {isLong && (
                        <button
                          className={styles.descToggleBtn}
                          onClick={() => setDescExpanded(v => !v)}
                        >
                          {descExpanded ? '▲ Свернуть' : '▼ Подробнее'}
                        </button>
                      )}
                    </div>
                  )
                })()}
                {selectedProduct.shop?.logo && (
                  <div className={styles.detailStoreBadge}>
                    <img
                      src={selectedProduct.shop.logo}
                      alt={selectedProduct.shop.name}
                      className={styles.detailStoreLogo}
                    />
                    <div className={styles.detailStoreInfo}>
                      <span className={styles.detailStoreLabel}>Продавец</span>
                      <span className={styles.detailStoreName}>{selectedProduct.shop.name}</span>
                    </div>
                  </div>
                )}
                <div className={styles.detailActions}>
                  {!isAuth ? (
                    <button
                      className={styles.detailAddBtn}
                      onClick={() => window.dispatchEvent(new Event('openAuth'))}
                    >
                      🔑 Войти для покупки
                    </button>
                  ) : (
                    <button
                      className={`${styles.detailAddBtn} ${addedItems.has(selectedProduct.id) ? styles.detailAddBtnDone : ''}`}
                      onClick={e => handleAddToCart(selectedProduct, e)}
                      disabled={selectedProduct.stock <= 0 || cartLoadingFor[selectedProduct.id]}
                    >
                      {cartLoadingFor[selectedProduct.id] ? 'Добавляется…'
                        : addedItems.has(selectedProduct.id) ? '✓ Добавлено в корзину'
                        : selectedProduct.stock <= 0 ? 'Нет в наличии'
                        : '+ В корзину'}
                    </button>
                  )}
                  {cartCount > 0 && (
                    <button className={styles.goCartBtn} onClick={() => setView('cart')}>
                      Перейти в корзину ({cartCount})
                    </button>
                  )}
                </div>
              </div>
            </div>
            {/* ─── REVIEW / FORUM SECTION ─── */}
            <ReviewSection
              productSlug={selectedProduct.slug}
              isAuth={isAuth}
            />
          </div>
        )}

        {/* ══ CART ══ */}
        {view === 'cart' && (
          <div className={styles.cartView}>
            <h2 className={styles.sectionTitle}>
              Корзина
              {cartCount > 0 && <span className={styles.sectionCount}>{cartCount} {cartCount === 1 ? 'товар' : cartCount < 5 ? 'товара' : 'товаров'}</span>}
            </h2>
            {!isAuth ? (
              <div className={styles.authNotice}>
                <p>Войдите в аккаунт, чтобы использовать корзину и оформлять заказы</p>
                <button onClick={() => window.dispatchEvent(new Event('openAuth'))} className={styles.authBtn}>Войти</button>
              </div>
            ) : cartCount === 0 ? (
              <div className={styles.stateBox}>
                <CartSvg />
                <p>Ваша корзина пуста</p>
                <button onClick={() => setView('catalog')} className={styles.shopBtn}>Перейти к покупкам</button>
              </div>
            ) : (
              <div className={styles.cartLayout}>
                <div className={styles.cartItems}>
                  {cart.items.map(item => {
                    const p = item.product || {}
                    const unitPrice = parseFloat(item.unit_price_snapshot || p.price || 0)
                    return (
                      <div key={item.id} className={styles.cartItem}>
                        <div className={styles.cartItemImg}>
                          <img src={p.main_image || PLACEHOLDER} alt={p.name} onError={e => { e.target.src = PLACEHOLDER }} />
                        </div>
                        <div className={styles.cartItemInfo}>
                          <div className={styles.cartItemName}>{p.name}</div>
                          <div className={styles.cartItemUnit}>{fmt(unitPrice)} за шт.</div>
                        </div>
                        <div className={styles.qtyControl}>
                          <button className={styles.qtyBtn} onClick={() => handleUpdateQty(item.id, item.quantity - 1)}>−</button>
                          <span className={styles.qtyNum}>{item.quantity}</span>
                          <button className={styles.qtyBtn} onClick={() => handleUpdateQty(item.id, item.quantity + 1)}>+</button>
                        </div>
                        <div className={styles.cartItemSub}>{fmt(unitPrice * item.quantity)}</div>
                        <button className={styles.removeBtn} onClick={() => handleRemoveItem(item.id)} title="Удалить">
                          <TrashSvg />
                        </button>
                      </div>
                    )
                  })}
                </div>
                <div className={styles.cartSummaryBox}>
                  <h3>Итого</h3>
                  <div className={styles.summaryRows}>
                    <div className={styles.summaryRow}>
                      <span>Товаров</span>
                      <span>{cartCount} шт.</span>
                    </div>
                    <div className={`${styles.summaryRow} ${styles.summaryTotal}`}>
                      <span>Сумма</span>
                      <span>{fmt(cartTotal)}</span>
                    </div>
                  </div>
                  <p className={styles.cartNote}>
                    Оплата при получении. Менеджер свяжется с вами для подтверждения.
                  </p>
                  <button className={styles.checkoutBtn} onClick={() => setView('checkout')}>
                    Оформить заказ →
                  </button>
                  <button className={styles.continueBtn} onClick={() => setView('catalog')}>
                    Продолжить покупки
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══ CHECKOUT ══ */}
        {view === 'checkout' && (
          <div className={styles.checkoutView}>
            <button className={styles.backBtn} onClick={() => setView('cart')}>← Назад в корзину</button>
            <div className={styles.checkoutLayout}>
              {/* Order summary panel */}
              <div className={styles.orderSummaryPanel}>
                <h3>Ваш заказ</h3>
                <div className={styles.orderSummaryItems}>
                  {cart.items.map(item => {
                    const p = item.product || {}
                    const price = parseFloat(item.unit_price_snapshot || p.price || 0)
                    return (
                      <div key={item.id} className={styles.summaryItem}>
                        <img src={p.main_image || PLACEHOLDER} alt={p.name} className={styles.summaryItemImg} onError={e => { e.target.src = PLACEHOLDER }} />
                        <div className={styles.summaryItemInfo}>
                          <div className={styles.summaryItemName}>{p.name}</div>
                          <div className={styles.summaryItemQty}>× {item.quantity}</div>
                        </div>
                        <div className={styles.summaryItemPrice}>{fmt(price * item.quantity)}</div>
                      </div>
                    )
                  })}
                </div>
                <div className={styles.summaryDivider} />
                <div className={styles.summaryFinal}>
                  <span>Итого</span>
                  <span className={styles.summaryFinalAmt}>{fmt(cartTotal)}</span>
                </div>
              </div>

              {/* Checkout form */}
              <div className={styles.checkoutForm}>
                <h2>Оформление заказа</h2>
                <p className={styles.formSubtitle}>
                  Заполните контактные данные. Менеджер свяжется для подтверждения.
                </p>

                <div className={styles.formGroup}>
                  <label>Ваше имя *</label>
                  <input type="text" placeholder="Бакытов Бакыт" value={checkoutForm.name}
                    onChange={e => setCheckoutForm(p => ({ ...p, name: e.target.value }))}
                    className={`${styles.formInput} ${checkoutErrors.name ? styles.formInputErr : ''}`} />
                  {checkoutErrors.name && <span className={styles.fieldErr}>{checkoutErrors.name}</span>}
                </div>

                <div className={styles.formGroup}>
                  <label>Номер телефона *</label>
                  <input type="tel" placeholder="+996 700 000 000" value={checkoutForm.phone}
                    onChange={e => setCheckoutForm(p => ({ ...p, phone: e.target.value }))}
                    className={`${styles.formInput} ${checkoutErrors.phone ? styles.formInputErr : ''}`} />
                  {checkoutErrors.phone && <span className={styles.fieldErr}>{checkoutErrors.phone}</span>}
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label>Город</label>
                    <input type="text" placeholder="Бишкек" value={checkoutForm.city}
                      onChange={e => setCheckoutForm(p => ({ ...p, city: e.target.value }))}
                      className={styles.formInput} />
                  </div>
                  <div className={styles.formGroup}>
                    <label>Кв / Офис</label>
                    <input type="text" placeholder="кв. 12" value={checkoutForm.apartment}
                      onChange={e => setCheckoutForm(p => ({ ...p, apartment: e.target.value }))}
                      className={styles.formInput} />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label>Адрес доставки *</label>
                  <input type="text" placeholder="ул. Манаса 1" value={checkoutForm.address}
                    onChange={e => setCheckoutForm(p => ({ ...p, address: e.target.value }))}
                    className={`${styles.formInput} ${checkoutErrors.address ? styles.formInputErr : ''}`} />
                  {checkoutErrors.address && <span className={styles.fieldErr}>{checkoutErrors.address}</span>}
                </div>

                <div className={styles.formGroup}>
                  <label>Примечание к заказу</label>
                  <textarea placeholder="Удобное время доставки, пожелания…" value={checkoutForm.notes}
                    onChange={e => setCheckoutForm(p => ({ ...p, notes: e.target.value }))}
                    className={styles.formTextarea} rows={3} />
                </div>

                {checkoutErrors.submit && <div className={styles.submitErr}>{checkoutErrors.submit}</div>}

                <button className={styles.submitOrderBtn} onClick={handleSubmitOrder} disabled={checkoutLoading}>
                  {checkoutLoading ? 'Отправляем заявку…' : 'Подтвердить заказ'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ══ SUCCESS ══ */}
        {view === 'success' && (
          <div className={styles.successView}>
            <div className={styles.successIconWrap}><CheckSvg /></div>
            <h2 className={styles.successTitle}>Заказ оформлен!</h2>
            {lastOrder && <div className={styles.successOrderNum}>Заказ № {lastOrder.id}</div>}
            <p className={styles.successText}>
              Наш менеджер свяжется с вами в ближайшее время для подтверждения заказа и уточнения деталей доставки.
            </p>
            <p className={styles.successNote}>
              Заказ сохранён в вашем <strong>личном кабинете</strong> в разделе «Мои заказы».
            </p>
            <div className={styles.successActions}>
              {isAuth && (
                <button className={styles.myOrdersBtn} onClick={() => { setView('orders'); loadOrders() }}>
                  Мои заказы
                </button>
              )}
              <button className={styles.continueBtn} onClick={() => setView('catalog')}>
                Продолжить покупки
              </button>
            </div>
          </div>
        )}

        {/* ══ ORDERS ══ */}
        {view === 'orders' && (
          <div className={styles.ordersView}>
            <h2 className={styles.sectionTitle}>Мои заказы</h2>
            {!isAuth ? (
              <div className={styles.authNotice}>
                <p>Войдите в аккаунт для просмотра истории заказов</p>
                <a href="/login" className={styles.authBtn}>Войти</a>
              </div>
            ) : ordersLoading ? (
              <div className={styles.loaderWrap}><Loader /></div>
            ) : orders.length === 0 ? (
              <div className={styles.stateBox}>
                <BoxSvg />
                <p>У вас пока нет заказов</p>
                <button onClick={() => setView('catalog')} className={styles.shopBtn}>Перейти в каталог</button>
              </div>
            ) : (
              <div className={styles.ordersList}>
                {orders.map(order => {
                  const st = STATUS_INFO[order.status] || { label: order.status, color: '#999' }
                  const date = fmtDate(order.created_at, { day: '2-digit', month: 'long', year: 'numeric' })
                  return (
                    <div key={order.id} className={styles.orderCard}>
                      <div className={styles.orderCardHead}>
                        <div className={styles.orderNum}>Заказ № {order.id}</div>
                        <div className={styles.orderStatus} style={{ color: st.color, borderColor: `${st.color}44` }}>
                          {st.label}
                        </div>
                        <div className={styles.orderDate}>{date}</div>
                      </div>
                      {order.items && order.items.length > 0 && (
                        <div className={styles.orderItems}>
                          {order.items.map(item => (
                            <div key={item.id} className={styles.orderItem}>
                              <span className={styles.orderItemName}>{item.product?.name || 'Товар'}</span>
                              <span className={styles.orderItemQty}>× {item.quantity}</span>
                              <span className={styles.orderItemPrice}>{fmt(parseFloat(item.unit_price || 0) * item.quantity)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className={styles.orderCardFoot}>
                        <div className={styles.orderTotal}>Итого: {fmt(order.total_amount)}</div>
                        {order.notes && (
                          <div className={styles.orderDelivery}>
                            <strong>Детали доставки:</strong>
                            <pre className={styles.orderNotes}>{order.notes}</pre>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ─── LIGHTBOX ─── */}
      {lightboxImg && (
        <div className={styles.lightboxOverlay} onClick={() => setLightboxImg(null)}>
          <button className={styles.lightboxClose} onClick={() => setLightboxImg(null)} aria-label="Закрыть">×</button>
          <img
            src={lightboxImg}
            alt=""
            className={styles.lightboxImg}
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}

      {/* ─── FLOATING CART ─── */}
      {isCatalogView && cartCount > 0 && (
        <button className={styles.floatingCart} onClick={() => setView('cart')}>
          <CartSvg />
          <span className={styles.floatingCount}>{cartCount}</span>
          <span className={styles.floatingTotal}>{fmt(cartTotal)}</span>
        </button>
      )}
    </div>
  )
}

export default ShopPage

/* ═══════════════════════════════════════════════
   REVIEW / FORUM SECTION
   ═══════════════════════════════════════════════ */

const STAR_GOLD = '#fbbf24'

function StarPicker({ value, hover, onHover, onLeave, onPick, size = 28 }) {
  const active = hover || value
  return (
    <div className={styles.starPicker}>
      {[1,2,3,4,5].map(n => {
        const lit = n <= active
        return (
          <button
            key={n}
            type="button"
            className={styles.starPickerBtn}
            style={lit ? { color: STAR_GOLD } : undefined}
            data-lit={lit || undefined}
            onMouseEnter={() => onHover(n)}
            onMouseLeave={onLeave}
            onClick={() => onPick(n)}
            aria-label={`${n} звезда`}
          >★</button>
        )
      })}
    </div>
  )
}

function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr)) / 1000
  if (diff < 60) return 'только что'
  if (diff < 3600) return `${Math.floor(diff / 60)} мин. назад`
  if (diff < 86400) return `${Math.floor(diff / 3600)} ч. назад`
  if (diff < 604800) return `${Math.floor(diff / 86400)} дн. назад`
  return new Date(dateStr).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' })
}

function Avatar({ src, username, size = 36 }) {
  const initials = (username || '?').slice(0, 2).toUpperCase()
  const hue = (username || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % 360

  if (src) return (
    <img
      src={src}
      alt={username}
      className={styles.rvAvatar}
      style={{ width: size, height: size }}
      onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex' }}
    />
  )
  return (
    <div
      className={styles.rvAvatarFallback}
      style={{
        width: size, height: size,
        background: `hsl(${hue},60%,45%)`,
      }}
    >{initials}</div>
  )
}

function CommentItem({ comment, isAuth, currentUsername, productSlug, onReload }) {
  const [showReply, setShowReply] = useState(false)
  const [replyBody, setReplyBody] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [pendingDeleteId, setPendingDeleteId] = useState(null) // id being confirmed for delete

  async function submitReply(e) {
    e.preventDefault()
    if (!replyBody.trim()) return
    setSubmitting(true)
    try {
      await addProductComment(productSlug, { body: replyBody, parent: comment.id })
      setReplyBody('')
      setShowReply(false)
      onReload()
    } catch {}
    setSubmitting(false)
  }

  async function confirmDelete() {
    if (!pendingDeleteId) return
    try {
      await deleteProductComment(productSlug, pendingDeleteId)
      onReload()
    } catch {}
    setPendingDeleteId(null)
  }

  return (
    <div className={styles.rvComment}>
      <div className={styles.rvCommentHead}>
        <Avatar src={comment.user_avatar} username={comment.user_username} />
        <div className={styles.rvCommentMeta}>
          <span className={styles.rvCommentUser}>{comment.user_username}</span>
          {comment.rating != null && (
            <span className={styles.rvCommentRating}>
              {[1,2,3,4,5].map(s => (
                <span key={s} style={{ color: s <= comment.rating ? STAR_GOLD : 'rgba(255,255,255,0.2)' }}>★</span>
              ))}
            </span>
          )}
          <span className={styles.rvCommentDate}>{timeAgo(comment.created_at)}</span>
        </div>
        {comment.is_mine && (
          <button className={styles.rvDeleteBtn} onClick={() => setPendingDeleteId(comment.id)} title="Удалить">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="m19 6-.867 14.142A2 2 0 0 1 16.138 22H7.862a2 2 0 0 1-1.995-1.858L5 6"/><path d="M10 11v6M14 11v6"/><path d="m9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
          </button>
        )}
      </div>
      <p className={styles.rvCommentBody}>{comment.body}</p>

      {/* Replies */}
      {comment.replies?.length > 0 && (
        <div className={styles.rvReplies}>
          {comment.replies.map(reply => (
            <div key={reply.id} className={styles.rvReply}>
              <div className={styles.rvCommentHead}>
                <Avatar src={reply.user_avatar} username={reply.user_username} size={28} />
                <div className={styles.rvCommentMeta}>
                  <span className={styles.rvCommentUser}>{reply.user_username}</span>
                  <span className={styles.rvCommentDate}>{timeAgo(reply.created_at)}</span>
                </div>
                {reply.is_mine && (
                  <button className={styles.rvDeleteBtn} onClick={() => setPendingDeleteId(reply.id)} title="Удалить">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="m19 6-.867 14.142A2 2 0 0 1 16.138 22H7.862a2 2 0 0 1-1.995-1.858L5 6"/><path d="M10 11v6M14 11v6"/><path d="m9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                  </button>
                )}
              </div>
              <p className={styles.rvCommentBody}>{reply.body}</p>
            </div>
          ))}
        </div>
      )}

      {/* Reply toggle */}
      {isAuth && !showReply && (
        <button className={styles.rvReplyToggle} onClick={() => setShowReply(true)}>↩ Ответить</button>
      )}
      {isAuth && showReply && (
        <form className={styles.rvReplyForm} onSubmit={submitReply}>
          <textarea
            className={styles.rvInput}
            placeholder={`Ответить ${comment.user_username}…`}
            value={replyBody}
            onChange={e => setReplyBody(e.target.value)}
            rows={2}
            maxLength={1000}
          />
          <div className={styles.rvReplyFormBtns}>
            <button type="submit" className={styles.rvSubmitBtn} disabled={!replyBody.trim() || submitting}>
              {submitting ? '…' : 'Отправить'}
            </button>
            <button type="button" className={styles.rvCancelBtn} onClick={() => { setShowReply(false); setReplyBody('') }}>Отмена</button>
          </div>
        </form>
      )}

      {/* Delete confirmation modal */}
      {pendingDeleteId && (
        <div className={styles.rvDeleteOverlay} onClick={() => setPendingDeleteId(null)}>
          <div className={styles.rvDeleteModal} onClick={e => e.stopPropagation()}>
            <div className={styles.rvDeleteIcon}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <polyline points="3 6 5 6 21 6"/>
                <path d="m19 6-.867 14.142A2 2 0 0 1 16.138 22H7.862a2 2 0 0 1-1.995-1.858L5 6"/>
                <path d="M10 11v6M14 11v6"/>
                <path d="m9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
              </svg>
            </div>
            <h4 className={styles.rvDeleteTitle}>Удалить комментарий?</h4>
            <p className={styles.rvDeleteSub}>Это действие нельзя отменить</p>
            <div className={styles.rvDeleteBtns}>
              <button className={styles.rvCancelBtn} onClick={() => setPendingDeleteId(null)}>Отмена</button>
              <button className={styles.rvConfirmDeleteBtn} onClick={confirmDelete}>Удалить</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function ReviewSection({ productSlug, isAuth }) {
  const [comments, setComments] = useState([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [body, setBody] = useState('')
  const [rating, setRating] = useState(0)
  const [hoverRating, setHoverRating] = useState(0)
  const [submitting, setSubmitting] = useState(false)
  const [submitErr, setSubmitErr] = useState('')

  const load = useCallback(async (pg = 1) => {
    setLoading(true)
    try {
      const res = await getProductComments(productSlug, pg)
      const data = res.data
      const results = data.results ?? data
      setComments(results)
      if (data.count && data.page_size) setTotalPages(Math.ceil(data.count / data.page_size))
      else setTotalPages(1)
    } catch {}
    setLoading(false)
  }, [productSlug])

  useEffect(() => { load(page) }, [productSlug, page, load])

  async function handleSubmit(e) {
    e.preventDefault()
    if (!body.trim()) return
    setSubmitErr('')
    setSubmitting(true)
    try {
      await addProductComment(productSlug, {
        body,
        rating: rating > 0 ? rating : null,
      })
      setBody('')
      setRating(0)
      setPage(1)
      await load(1)
    } catch (err) {
      const msg = err?.response?.data?.body?.[0] || err?.response?.data?.detail || 'Ошибка при отправке'
      setSubmitErr(msg)
    }
    setSubmitting(false)
  }

  // Rating distribution from loaded comments (top-level with rating)
  const ratedComments = comments.filter(c => c.rating != null)
  const avgRating = ratedComments.length
    ? (ratedComments.reduce((s, c) => s + c.rating, 0) / ratedComments.length).toFixed(1)
    : null
  const dist = [5,4,3,2,1].map(s => ({
    star: s,
    count: ratedComments.filter(c => c.rating === s).length,
    pct: ratedComments.length ? Math.round(ratedComments.filter(c => c.rating === s).length / ratedComments.length * 100) : 0,
  }))

  return (
    <div className={styles.rvSection}>
      <h3 className={styles.rvTitle}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
        Обсуждение
        {comments.length > 0 && <span className={styles.rvTitleCount}>{comments.length}</span>}
      </h3>

      {/* Rating summary */}
      {ratedComments.length > 0 && (
        <div className={styles.rvRatingSummary}>
          <div className={styles.rvRatingBig}>
            <span className={styles.rvRatingNum}>{avgRating}</span>
            <div className={styles.rvRatingStars}>
              {[1,2,3,4,5].map(s => (
                <span key={s} style={{ color: s <= Math.round(avgRating) ? '#fbbf24' : 'rgba(255,255,255,0.18)', fontSize: 20 }}>★</span>
              ))}
            </div>
            <span className={styles.rvRatingTotal}>{ratedComments.length} {ratedComments.length === 1 ? 'оценка' : ratedComments.length < 5 ? 'оценки' : 'оценок'}</span>
          </div>
          <div className={styles.rvRatingBars}>
            {dist.map(({ star, count, pct }) => (
              <div key={star} className={styles.rvRatingBar}>
                <span className={styles.rvBarStar}>{star}★</span>
                <div className={styles.rvBarTrack}>
                  <div className={styles.rvBarFill} style={{ width: `${pct}%`, background: STAR_GOLD }} />
                </div>
                <span className={styles.rvBarCount}>{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Write a comment */}
      {isAuth ? (
        <form className={styles.rvForm} onSubmit={handleSubmit}>
          <div className={styles.rvFormHeader}>
            <span className={styles.rvFormLabel}>Оставить отзыв</span>
            <StarPicker
              value={rating}
              hover={hoverRating}
              onHover={setHoverRating}
              onLeave={() => setHoverRating(0)}
              onPick={n => setRating(prev => prev === n ? 0 : n)}
            />
          </div>
          <textarea
            className={styles.rvInput}
            placeholder="Напишите ваш отзыв или вопрос…"
            value={body}
            onChange={e => setBody(e.target.value)}
            rows={3}
            maxLength={2000}
          />
          {submitErr && <div className={styles.rvErr}>{submitErr}</div>}
          <div className={styles.rvFormFooter}>
            <span className={styles.rvCharCount}>{body.length}/2000</span>
            <button type="submit" className={styles.rvSubmitBtn} disabled={!body.trim() || submitting}>
              {submitting ? '…' : 'Опубликовать'}
            </button>
          </div>
        </form>
      ) : (
        <div className={styles.rvAuthNotice}>
          <span>Войдите, чтобы оставить отзыв</span>
          <button className={styles.rvAuthBtn} onClick={() => window.dispatchEvent(new Event('openAuth'))}>
            Войти
          </button>
        </div>
      )}

      {/* Comment list */}
      <div className={styles.rvList}>
        {loading ? (
          <div className={styles.rvLoading}><Loader /></div>
        ) : comments.length === 0 ? (
          <div className={styles.rvEmpty}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            <p>Пока нет отзывов. Будьте первым!</p>
          </div>
        ) : (
          comments.map(c => (
            <CommentItem
              key={c.id}
              comment={c}
              isAuth={isAuth}
              productSlug={productSlug}
              onReload={() => load(page)}
            />
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className={styles.rvPagination}>
          <button className={styles.rvPageBtn} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Назад</button>
          <span className={styles.rvPageInfo}>{page} / {totalPages}</span>
          <button className={styles.rvPageBtn} disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Далее →</button>
        </div>
      )}
    </div>
  )
}